import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { apiService } from '../services/api';
import { 
  Search,
  Plus,
  Edit,
  Trash2,
  X,
  Save,
  ExternalLink,
  Users,
  Activity
} from 'lucide-react';

const ConnectedUserAccounts = () => {
  const navigate = useNavigate();
  // Check for tab parameter in URL - no longer needed but keeping for consistency
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedExchange, setSelectedExchange] = useState('');
  const [selectedMode, setSelectedMode] = useState(''); // paper, live, or '' for all
  
  // Data states
  const [userAccounts, setUserAccounts] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // User search states
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [searchedUsers, setSearchedUsers] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  
  // Stats state
  const [stats, setStats] = useState({
    totalExchanges: 0,
    totalInstruments: 0,
    connectedUsers: 0,
    activeConnections: 0
  });
  const [loading, setLoading] = useState(true);
  
  // Connection status tracking
  const [connectionStatuses, setConnectionStatuses] = useState({});

  useEffect(() => {
    fetchAllData();
  }, []);

  // Helper function to check broker connection status (supports all brokers with API)
  const checkBrokerConnection = async (item) => {
    // Check if this account supports broker API
    const brokerSlug = item.broker_slug || item.exchange?.broker_slug;
    if (!brokerSlug) {
      return 'N/A';
    }

    try {
      // Use the backend endpoint to check broker connection
      const response = await apiService.checkBrokerConnection({
        user_exchange_account_id: item.id
      });
      
      const data = response.data;
      return data.status === 'connected' ? 'Connected' : 'Disconnected';
    } catch (error) {
      console.error('Error checking IB Gateway connection:', error);
      return 'Error';
    }
  };

  // Check connection status for all IB accounts
  useEffect(() => {
    const checkAllConnections = async () => {
      const newStatuses = {};
      
      for (const item of userAccounts) {
        newStatuses[item.id] = await checkBrokerConnection(item);
      }
      
      setConnectionStatuses(newStatuses);
    };

    if (userAccounts.length > 0) {
      checkAllConnections();
      
      // Set up periodic checking every 30 seconds for IB accounts
      const interval = setInterval(checkAllConnections, 30000);
      return () => clearInterval(interval);
    }
  }, [userAccounts]);

  // Debounced user search
  useEffect(() => {
    if (!userSearchTerm || userSearchTerm.length < 2) {
      setSearchedUsers([]);
      setUserSearchLoading(false);
      return;
    }

    setUserSearchLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        // Try custom users first, fallback to regular users
        let response;
        try {
          response = await apiService.getCustomUsers({ 
            search: userSearchTerm,
            page_size: 10 
          });
        } catch (error) {
          // Fallback to regular users endpoint
          response = await apiService.getUsers({ 
            search: userSearchTerm,
            page_size: 10 
          });
        }
        
        const users = response.data.results || response.data || [];
        setSearchedUsers(users);
      } catch (error) {
        console.error('Error searching users:', error);
        setSearchedUsers([]);
      } finally {
        setUserSearchLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [userSearchTerm]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      const [exchangesRes, userAccountsRes, usersRes] = await Promise.all([
        apiService.getExchanges().catch(() => ({ data: [] })),
        apiService.getUserExchangeAccounts().catch(() => ({ data: [] })),
        apiService.getCustomUsers?.().catch(() => ({ data: [] })) || Promise.resolve({ data: [] })
      ]);

      const exchangesData = exchangesRes.data.results || exchangesRes.data || [];
      const userAccountsData = userAccountsRes.data.results || userAccountsRes.data || [];
      const usersData = usersRes.data.results || usersRes.data || [];

      setExchanges(exchangesData);
      setUserAccounts(userAccountsData);
      setUsers(usersData);

      // Calculate stats
      const connectedUsers = new Set(userAccountsData.map(acc => acc.customuser?.id || acc.customuser_id)).size;
      const activeConnections = userAccountsData.filter(acc => acc.is_active).length;

      setStats({
        totalExchanges: exchangesData.length,
        totalInstruments: 0, // Not relevant for this page
        connectedUsers,
        activeConnections
      });

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredData = () => {
    let filteredData = userAccounts;

    // Filter by exchange if selected
    if (selectedExchange) {
      filteredData = filteredData.filter(item => {
        const exchangeId = item.exchange?.id || item.exchange_id;
        return exchangeId && exchangeId.toString() === selectedExchange;
      });
    }

    // Filter by mode if selected
    if (selectedMode) {
      filteredData = filteredData.filter(item => {
        return item.mode === selectedMode;
      });
    }

    // Filter by search term
    if (searchTerm) {
      filteredData = filteredData.filter(item => {
        return (item.customuser_email || item.customuser?.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
               (item.exchange_name || item.exchange?.name || '').toLowerCase().includes(searchTerm.toLowerCase());
      });
    }
    
    return filteredData;
  };

  const openModal = async (mode, item = null) => {
    setModalMode(mode);
    setSelectedItem(item);
    setFormErrors({});
    
    if (mode === 'create') {
      setFormData(getEmptyFormData());
      setUserSearchTerm('');
      setSearchedUsers([]);
      setShowModal(true);
    } else {
      // For edit mode, fetch the complete user exchange account data
      try {
        const response = await apiService.getUserExchangeAccount(item.id);
        const fullItem = response.data;
        
        // Find the exchange ID by matching exchange name
        let exchangeId = '';
        if (fullItem.exchange?.id) {
          exchangeId = fullItem.exchange.id.toString();
        } else if (item.exchange_name) {
          // Find exchange by name from the available exchanges
          const matchedExchange = exchanges.find(ex => ex.name === item.exchange_name);
          if (matchedExchange) {
            exchangeId = matchedExchange.id.toString();
          }
        }
        
        // Transform the complete API response data to match form field expectations
        const mappedFormData = {
          id: fullItem.id,
          customuser_id: (fullItem.customuser?.id || fullItem.customuser_id || fullItem.customuser || '').toString(),
          exchange_id: exchangeId,
          mode: fullItem.mode || 'paper',
          api_key: '', // Always start empty for security, but track if it exists
          api_secret: '', // Always start empty for security, but track if it exists
          label: fullItem.label || '',
          is_active: fullItem.is_active !== false,
          // Keep track of whether fields have existing values (from backend)
          has_api_key: fullItem.has_api_key || false,
          has_api_secret: fullItem.has_api_secret || false
        };
        
        setFormData(mappedFormData);
        
        // Set up user search
        const userEmail = fullItem?.customuser?.email || item?.customuser_email;
        if (userEmail) {
          setUserSearchTerm(userEmail);
          // Pre-populate with the current user
          const userObj = fullItem.customuser || { 
            id: fullItem.customuser?.id || fullItem.customuser_id || fullItem.customuser,
            email: userEmail,
            first_name: fullItem.customuser?.first_name || '',
            last_name: fullItem.customuser?.last_name || ''
          };
          setSearchedUsers([userObj]);
        }
        
        setShowModal(true);
      } catch (error) {
        console.error('Error fetching complete user exchange account data:', error);
        // Fallback to using the summary data if detailed fetch fails
        alert('Error loading account details: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    setFormData({});
    setFormErrors({});
    setUserSearchTerm('');
    setShowUserDropdown(false);
    setSearchedUsers([]);
    setUserSearchLoading(false);
  };

  const getFilteredUsers = () => {
    // Use searched users from backend API call
    return searchedUsers;
  };

  const handleUserSelect = (user) => {
    setFormData({...formData, customuser_id: user.id});
    setUserSearchTerm(user.email);
    setShowUserDropdown(false);
  };

  const getEmptyFormData = () => {
    return {
      customuser_id: '',
      exchange_id: '',
      mode: 'paper',
      api_key: '',
      api_secret: '',
      label: '',
      is_active: true
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormErrors({});

    // Check for duplicates on create
    if (modalMode === 'create') {
      const existingAccount = userAccounts.find(account => 
        account.customuser_id === formData.customuser_id &&
        account.exchange_id === parseInt(formData.exchange_id) &&
        account.mode === formData.mode
      );

      if (existingAccount) {
        const exchangeName = exchanges.find(ex => ex.id.toString() === formData.exchange_id)?.name || 'this exchange';
        setFormErrors({
          submit: `This user already has a ${formData.mode} trading account on ${exchangeName}. Business rule: Each user can have only ONE paper account and ONE live account per exchange.`
        });
        setSubmitting(false);
        return;
      }
    }

    try {
      let response;
      let submitData = { ...formData };
      
      // For edit mode, if password fields are empty, don't send them (keep existing values)
      if (modalMode === 'edit') {
        if (!submitData.api_key && formData.has_api_key) {
          delete submitData.api_key;
        }
        if (!submitData.api_secret && formData.has_api_secret) {
          delete submitData.api_secret;
        }
        // Remove helper fields
        delete submitData.has_api_key;
        delete submitData.has_api_secret;
      }
      
      if (modalMode === 'create') {
        response = await apiService.createUserExchangeAccount(submitData);
        setUserAccounts([...userAccounts, response.data]);
      } else {
        response = await apiService.updateUserExchangeAccount(selectedItem.id, submitData);
        setUserAccounts(userAccounts.map(acc => acc.id === selectedItem.id ? response.data : acc));
      }

      closeModal();
      // Refresh stats
      fetchAllData();
      
    } catch (error) {
      console.error('Error saving:', error);
      if (error.response?.data) {
        setFormErrors(error.response.data);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Are you sure you want to delete this user account?')) return;

    try {
      await apiService.deleteUserExchangeAccount(item.id);
      setUserAccounts(userAccounts.filter(acc => acc.id !== item.id));
      
      // Refresh stats
      fetchAllData();
      
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Error deleting user account');
    }
  };

  const renderModal = () => {
    if (!showModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-white">
              {modalMode === 'create' ? 'Add Connected User Account' : 'Edit Connected User Account'}
            </h3>
            <button onClick={closeModal} className="text-gray-400 hover:text-white">
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Mode Selection - FIRST FIELD */}
            <div className={`p-4 rounded-lg border-2 ${
              formData.mode === 'live' 
                ? 'border-red-500 bg-red-500/5' 
                : 'border-yellow-500 bg-yellow-500/5'
            }`}>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Trading Mode * {modalMode === 'edit' ? '(Cannot be changed after creation)' : '(Critical Selection)'}
              </label>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => modalMode === 'create' && setFormData({...formData, mode: 'paper'})}
                  disabled={modalMode === 'edit'}
                  className={`flex-1 p-3 rounded-lg border-2 text-left transition-all ${
                    formData.mode === 'paper'
                      ? 'border-yellow-500 bg-yellow-500/20 text-yellow-300'
                      : modalMode === 'edit'
                      ? 'border-gray-600 bg-gray-800 text-gray-500 cursor-not-allowed'
                      : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-yellow-500/50'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${formData.mode === 'paper' ? 'bg-yellow-400' : 'bg-gray-500'}`}></div>
                    <div>
                      <div className="font-medium">Paper Trading</div>
                      <div className="text-xs opacity-75">Simulation with fake money</div>
                    </div>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => modalMode === 'create' && setFormData({...formData, mode: 'live'})}
                  disabled={modalMode === 'edit'}
                  className={`flex-1 p-3 rounded-lg border-2 text-left transition-all ${
                    formData.mode === 'live'
                      ? 'border-red-500 bg-red-500/20 text-red-300'
                      : modalMode === 'edit'
                      ? 'border-gray-600 bg-gray-800 text-gray-500 cursor-not-allowed'
                      : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-red-500/50'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${formData.mode === 'live' ? 'bg-red-400' : 'bg-gray-500'}`}></div>
                    <div>
                      <div className="font-medium">Live Trading</div>
                      <div className="text-xs opacity-75">Real money & markets</div>
                    </div>
                  </div>
                </button>
              </div>
              {modalMode === 'edit' && (
                <div className="mt-2 p-2 bg-blue-500/10 border border-blue-500/20 rounded text-blue-400 text-xs">
                  🔒 Trading mode cannot be changed for security reasons. Create a new account for different mode.
                </div>
              )}
              {formData.mode === 'live' && modalMode === 'create' && (
                <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-xs">
                  ⚠️ WARNING: Live mode uses real money and live markets. Double-check all settings.
                </div>
              )}
            </div>

            <div className="relative user-search-dropdown">
              <label className="block text-sm font-medium text-gray-300 mb-1">User</label>
              {modalMode === 'edit' ? (
                <input
                  type="text"
                  value={userSearchTerm}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-600 rounded-lg text-gray-300 cursor-not-allowed"
                  disabled
                  readOnly
                />
              ) : (
                <>
                  <input
                    type="text"
                    value={userSearchTerm}
                    onChange={(e) => {
                      setUserSearchTerm(e.target.value);
                      setShowUserDropdown(true);
                      if (!e.target.value) {
                        setFormData({...formData, customuser_id: ''});
                      }
                    }}
                    onFocus={() => setShowUserDropdown(true)}
                    onBlur={() => {
                      // Delay hiding to allow for clicks on suggestions
                      setTimeout(() => setShowUserDropdown(false), 200);
                    }}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    placeholder="Search by email or name..."
                    required
                  />
                  {formErrors.customuser_id && <p className="text-red-400 text-xs mt-1">{formErrors.customuser_id[0]}</p>}
                  
                  {showUserDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {userSearchLoading ? (
                        <div className="px-3 py-2 text-gray-400 flex items-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                          <span>Searching...</span>
                        </div>
                      ) : userSearchTerm.length < 2 ? (
                        <div className="px-3 py-2 text-gray-400">Type at least 2 characters to search</div>
                      ) : getFilteredUsers().length > 0 ? (
                        getFilteredUsers().map(user => (
                          <div
                            key={user.id}
                            onClick={() => handleUserSelect(user)}
                            className="px-3 py-2 hover:bg-gray-600 cursor-pointer text-white"
                          >
                            <div className="font-medium">{user.email}</div>
                            {(user.first_name || user.last_name) && (
                              <div className="text-sm text-gray-400">
                                {user.first_name} {user.last_name}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-gray-400">No users found</div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Exchange</label>
              <select
                value={formData.exchange_id || ''}
                onChange={(e) => setFormData({...formData, exchange_id: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                required
              >
                <option value="">Select Exchange</option>
                {exchanges.map(ex => <option key={ex.id} value={ex.id.toString()}>{ex.name}</option>)}
              </select>
            </div>


            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">API Key</label>
              <input
                type="password"
                value={formData.api_key || ''}
                onChange={(e) => setFormData({...formData, api_key: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                placeholder={formData.has_api_key === true && modalMode === 'edit' ? '••••••••••••••••' : 'Enter API key'}
              />
              {formData.has_api_key && modalMode === 'edit' && (
                <p className="text-xs text-gray-400 mt-1">Leave blank to keep existing API key</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">API Secret</label>
              <input
                type="password"
                value={formData.api_secret || ''}
                onChange={(e) => setFormData({...formData, api_secret: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                placeholder={formData.has_api_secret === true && modalMode === 'edit' ? '••••••••••••••••' : 'Enter API secret'}
              />
              {formData.has_api_secret && modalMode === 'edit' && (
                <p className="text-xs text-gray-400 mt-1">Leave blank to keep existing API secret</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Label (Optional)</label>
              <input
                type="text"
                value={formData.label || ''}
                onChange={(e) => setFormData({...formData, label: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                placeholder="e.g., My Binance Account"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active !== false}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="mr-2"
              />
              <label htmlFor="is_active" className="text-sm text-gray-300">Active</label>
            </div>
            
            {/* Submit Error Display */}
            {formErrors.submit && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-400 text-sm flex items-start">
                  <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                  </svg>
                  {formErrors.submit}
                </p>
              </div>
            )}
            
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={closeModal}
                className="flex-1 px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderTable = () => {
    const data = getFilteredData();

    return (
      <table className="w-full">
        <thead className="bg-gray-700/50">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">User</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Exchange</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Mode</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Label</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Connection</th>
            <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700">
          {loading ? (
            <tr><td colSpan="7" className="px-6 py-12 text-center text-gray-400">Loading...</td></tr>
          ) : data.length === 0 ? (
            <tr><td colSpan="7" className="px-6 py-12 text-center text-gray-400">No user accounts found</td></tr>
          ) : (
            data.map((item) => (
              <tr key={item.id} className="hover:bg-gray-700/30">
                <td className="px-6 py-4 text-gray-300">{item.customuser_email || item.customuser?.email || '-'}</td>
                <td className="px-6 py-4 text-gray-300">{item.exchange_name || item.exchange?.name || '-'}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded text-xs font-medium flex items-center space-x-1 w-fit ${
                    item.mode === 'live'
                      ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                      : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                  }`}>
                    <div className={`w-2 h-2 rounded-full ${
                      item.mode === 'live' ? 'bg-red-400' : 'bg-yellow-400'
                    }`}></div>
                    <span>{item.mode}</span>
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-300">{item.label || '-'}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded text-xs ${
                    item.is_active ? 'bg-green-500/10 text-green-400' : 'bg-gray-500/10 text-gray-400'
                  }`}>
                    {item.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  {(() => {
                    const brokerSlug = item.broker_slug || item.exchange?.broker_slug;
                    if (!brokerSlug) {
                      return <span className="text-gray-500 text-xs">N/A</span>;
                    }
                    
                    const status = connectionStatuses[item.id] || 'Checking...';
                    const statusColors = {
                      'Connected': 'bg-green-500/10 text-green-400',
                      'Disconnected': 'bg-red-500/10 text-red-400',
                      'Error': 'bg-red-500/10 text-red-400',
                      'Timeout': 'bg-yellow-500/10 text-yellow-400',
                      'Checking...': 'bg-blue-500/10 text-blue-400',
                      'N/A': 'bg-gray-500/10 text-gray-400'
                    };
                    
                    return (
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded text-xs ${statusColors[status] || 'bg-gray-500/10 text-gray-400'}`}>
                          {status}
                        </span>
                        {status === 'Disconnected' && (
                          <div className="text-xs text-gray-400">
                            Port: {item.mode === 'paper' ? '4002' : '4001'}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-6 py-4">
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => navigate(`/connected-user-accounts/${item.id}`)} 
                      className="text-green-400 hover:text-green-300" 
                      title="View Details"
                    >
                      <ExternalLink size={16} />
                    </button>
                    <button onClick={() => openModal('edit', item)} className="text-blue-400 hover:text-blue-300">
                      <Edit size={16} />
                    </button>
                    <button onClick={() => handleDelete(item)} className="text-red-400 hover:text-red-300">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white">Connected User Accounts</h1>
          <p className="text-gray-400 mt-1">Manage user exchange account connections and API credentials</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Search className="w-6 h-6 text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Total Exchanges</p>
                <p className="text-2xl font-bold text-white">{stats.totalExchanges}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <Users className="w-6 h-6 text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Connected Users</p>
                <p className="text-2xl font-bold text-white">{stats.connectedUsers}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <Activity className="w-6 h-6 text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Active Connections</p>
                <p className="text-2xl font-bold text-white">{stats.activeConnections}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-500/10 rounded-lg">
                <Activity className="w-6 h-6 text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-400">Success Rate</p>
                <p className="text-2xl font-bold text-white">
                  {stats.activeConnections > 0 ? Math.round((stats.activeConnections / userAccounts.length) * 100) : 0}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex justify-between items-center">
          <div className="flex space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search accounts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="flex space-x-3">
              <div className="relative">
                <select
                  value={selectedExchange}
                  onChange={(e) => setSelectedExchange(e.target.value)}
                  className="pl-3 pr-8 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Exchanges</option>
                  {exchanges.map(exchange => (
                    <option key={exchange.id} value={exchange.id}>{exchange.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Paper vs Live Mode Filter */}
              <div className="flex items-center space-x-1 bg-gray-800/50 border border-gray-700 rounded-lg">
                <button
                  onClick={() => setSelectedMode('')}
                  className={`px-3 py-2 text-sm font-medium transition-all ${
                    selectedMode === ''
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  All Modes
                </button>
                <button
                  onClick={() => setSelectedMode('paper')}
                  className={`px-3 py-2 text-sm font-medium transition-all flex items-center space-x-1 ${
                    selectedMode === 'paper'
                      ? 'bg-yellow-600 text-white'
                      : 'text-gray-300 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  <div className={`w-2 h-2 rounded-full ${selectedMode === 'paper' ? 'bg-yellow-300' : 'bg-yellow-500/50'}`}></div>
                  <span>Paper</span>
                </button>
                <button
                  onClick={() => setSelectedMode('live')}
                  className={`px-3 py-2 text-sm font-medium transition-all flex items-center space-x-1 ${
                    selectedMode === 'live'
                      ? 'bg-red-600 text-white'
                      : 'text-gray-300 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  <div className={`w-2 h-2 rounded-full ${selectedMode === 'live' ? 'bg-red-300' : 'bg-red-500/50'}`}></div>
                  <span>Live</span>
                </button>
              </div>
            </div>
          </div>
          
          <button 
            onClick={() => openModal('create')}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            <Plus size={20} />
            <span>Add Connected User Account</span>
          </button>
        </div>

        {/* Table */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg overflow-hidden">
          <div className="p-6 border-b border-gray-700">
            <h3 className="text-xl font-semibold text-white">All Connected User Accounts</h3>
          </div>
          
          <div className="overflow-x-auto">
            {renderTable()}
          </div>
        </div>

        {/* Modal */}
        {renderModal()}
      </div>
    </Layout>
  );
};

export default ConnectedUserAccounts;